import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Chip, Banner } from '../components/ui.jsx';
import { ghs } from '../lib/money.js';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

/**
 * The roll-up across all six.
 *
 * Everything here is takings. EPMS knows what came in and does not know the rent, so
 * nothing on this screen calls itself profit, however much a big number would like to.
 */
export function Dashboard() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.dashboard().then(setD).catch(e => setError(e.data?.error || 'failed'));
  }, []);

  if (error) return <Banner tone="danger">The roll-up did not load.</Banner>;
  if (!d) return <div className="t-body-m muted">Adding up all six businesses</div>;

  const dayChange = d.totals.yesterday
    ? Math.round(((d.totals.today - d.totals.yesterday) / d.totals.yesterday) * 100) : null;
  const monthChange = d.totals.lastMonth
    ? Math.round(((d.totals.month - d.totals.lastMonth) / d.totals.lastMonth) * 100) : null;

  const busiest = [...d.businesses].sort((a, b) => b.revenue - a.revenue);
  const most = busiest[0]?.revenue || 1;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div className="t-body-m secondary">
        Money taken, not money kept. Rent, wages and stock are not in EPMS, so nothing here
        is profit.
      </div>

      {d.attention.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {d.attention.map((a, i) => <Chip key={i} tone={a.tone}>{a.text}</Chip>)}
        </div>
      )}

      <div className="fig-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <Big label="TODAY" value={d.totals.today} sub={`${d.totals.salesToday} sale${d.totals.salesToday === 1 ? '' : 's'}`}
          change={dayChange} changeLabel="on yesterday" />
        <Big label="THIS WEEK" value={d.totals.week} sub={d.periods.week.label} />
        <Big label="THIS MONTH" value={d.totals.month} sub={d.periods.month.label}
          change={monthChange} changeLabel="on last month" />
        <Big label="LAST MONTH" value={d.totals.lastMonth} sub="for comparison" quiet />
      </div>

      <div style={card}>
        <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
          BY BUSINESS, THIS MONTH
        </div>
        {busiest.map(b => (
          <div key={b.id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 16px', borderTop: '1px solid var(--color-border-default)' }}>
            <span style={{ width: 4, height: 30, borderRadius: 999, background: b.colour || 'var(--color-border-strong)', flex: 'none' }} />
            <span className="t-body-m" style={{ width: 150, flex: 'none' }}>{b.short_name}</span>

            {/* One bar per business against the biggest of them, which is the only honest
                comparison when the six are on completely different scales. */}
            <span style={{ flex: 1, minWidth: 80, height: 8, background: 'var(--color-bg-subtle)', borderRadius: 999, overflow: 'hidden' }}>
              <span style={{
                display: 'block', height: '100%', borderRadius: 999,
                width: `${Math.max(1, Math.round((b.revenue / most) * 100))}%`,
                background: b.colour || 'var(--color-accent-default)'
              }} />
            </span>

            <span className="money t-money-s muted" style={{ width: 110, flex: 'none', textAlign: 'right' }}>
              {b.today ? ghs(b.today) : 'nothing yet'}
            </span>
            <span className="money t-money-s muted" style={{ width: 110, flex: 'none', textAlign: 'right' }}>{ghs(b.week)}</span>
            <span className="money t-money-m" style={{ width: 140, flex: 'none', textAlign: 'right' }}>{ghs(b.revenue)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 14, padding: '10px 16px', borderTop: '1px solid var(--color-border-default)' }} className="t-label-xs muted">
          <span style={{ flex: 1 }} />
          <span style={{ width: 110, textAlign: 'right' }}>TODAY</span>
          <span style={{ width: 110, textAlign: 'right' }}>WEEK</span>
          <span style={{ width: 140, textAlign: 'right' }}>MONTH</span>
        </div>
      </div>

      {d.payments.length > 0 && (
        <div style={card}>
          <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
            HOW PEOPLE PAID, THIS MONTH
          </div>
          {d.payments.map(p => (
            <div key={p.method} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--color-border-default)' }}>
              <span className="t-body-m" style={{ width: 150, textTransform: 'capitalize' }}>{p.method}</span>
              <span className="t-body-s muted" style={{ flex: 1 }}>{p.count} payment{p.count === 1 ? '' : 's'}</span>
              <span className="money t-money-m">{ghs(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Big({ label, value, sub, change, changeLabel, quiet }) {
  return (
    <div style={{ ...card, padding: 18 }}>
      <div className="t-label-xs muted">{label}</div>
      <div className={quiet ? 'money t-money-l muted' : 'money t-money-xl'} style={{ margin: '4px 0 2px' }}>
        {ghs(value)}
      </div>
      <div className="t-body-s muted">
        {sub}
        {change !== null && change !== undefined && (
          <span style={{ color: change >= 0 ? 'var(--color-status-success)' : 'var(--color-status-danger)' }}>
            {'  '}{change >= 0 ? '+' : ''}{change}% {changeLabel}
          </span>
        )}
      </div>
    </div>
  );
}
